import {
  ALERT_CRITICALITY_LABELS,
  PROJECT_PHASE_LABELS,
  PROJECT_PHASES,
  RISK_IMPACT_LABELS,
  RISK_PROBABILITY_LABELS,
  TRAFFIC_LIGHT_LABELS,
  currentOrganizationSchema,
  formatDate,
  formatDateTime,
  formatDecimal,
  overviewSchema,
  targetPeriodLabel,
  type Overview,
  type ProjectSummary,
  type TrafficLight,
} from '@fluxus/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowRight,
  ChevronRight,
  ArrowUpRight,
  CalendarRange,
  CalendarClock,
  CheckCircle2,
  FolderKanban,
  Gauge,
  ListChecks,
  Minus,
  Rocket,
  ShieldAlert,
  Target,
  TrendingDown,
  type LucideIcon,
} from 'lucide-react';
import { useId, type ReactNode } from 'react';
import { Link } from 'react-router';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { STATUS_DOT, StatusBadge } from '@/components/status/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/select';
import { drillHref, perspectiveTarget, usePeriodParam, worstPerspective } from '@/features/drill/drill';
import { DRILL_NUMBER, DrillValueLink } from '@/features/drill/DrillLinks';
import { firstName, greetingFor } from '@/features/home/greeting';
import { actionLink, meetingLink } from '@/features/meetings/CriticalAnalysis';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { cn } from '@/lib/cn';

const pct = (v: number | null) => (v === null ? '—' : `${formatDecimal(v, 1)}%`);
const monthLabel = (year: number, month: number) => `${targetPeriodLabel('MONTH', month)}/${year}`;

/**
 * Visão Geral da Empresa (T-140b, RF-09, RNF-14; DEC-054): o que precisa de atenção primeiro, depois a performance da
 * empresa com a tendência, as perspectivas e os números da estratégia — tudo dos agregados pré-calculados. Cada item
 * leva ao detalhe.
 */
export function VisaoGeralPage() {
  const { client } = useAuth();
  const org = useQuery({
    queryKey: ['organizations', 'current'],
    queryFn: ({ signal }) =>
      client.request('/organizations/current', { schema: currentOrganizationSchema, signal }),
  });

  if (org.isPending) {
    return (
      <PageContainer>
        <LoadingState rows={3} label="Carregando a visão geral" />
      </PageContainer>
    );
  }
  if (org.isError) {
    return (
      <PageContainer>
        <ErrorState error={org.error} onRetry={() => void org.refetch()} retrying={org.isFetching} />
      </PageContainer>
    );
  }
  const project = org.data.activeProject;
  return (
    <PageContainer>
      <PageHeader title={org.data.name} description={org.data.legalName ?? 'Visão Geral da empresa'} />
      {project ? (
        <>
          <ProjectOverview
            organizationId={org.data.id}
            projectId={project.id}
            horizon={[project.horizonStart, project.horizonEnd]}
          />
          <ProjectStrip project={project} />
        </>
      ) : (
        <section className="mt-8 rounded-lg border border-border bg-surface">
          <EmptyState
            icon={FolderKanban}
            title="Nenhum projeto BSC em andamento"
            description="A Fluxus cadastra o projeto BSC da sua empresa. Quando ele começar, a performance, os indicadores e o que precisa de atenção aparecem aqui."
          />
        </section>
      )}
    </PageContainer>
  );
}

/** O projeto BSC em andamento: nome, horizonte e as fases (T-022), no pé da página. */
function ProjectStrip({ project }: { project: ProjectSummary }) {
  const phaseIndex = PROJECT_PHASES.indexOf(project.phase);
  return (
    <section aria-labelledby="projeto-bsc" className="mt-8 rounded-lg border border-border bg-surface p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id="projeto-bsc" className="text-sm font-semibold text-muted-foreground">
            Projeto BSC em andamento
          </h2>
          <p className="mt-1 text-lg font-extrabold tracking-tight">{project.name}</p>
        </div>
        <Badge variant="brand">
          <FolderKanban aria-hidden />
          {PROJECT_PHASE_LABELS[project.phase]}
        </Badge>
      </div>
      <p className="mt-3 flex items-center gap-2 text-sm">
        <CalendarRange className="size-4 text-muted-foreground" aria-hidden />
        Horizonte estratégico de{' '}
        <strong className="font-bold">
          {project.horizonStart} a {project.horizonEnd}
        </strong>
      </p>
      <ol aria-label="Fases do projeto" className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {PROJECT_PHASES.map((phase, i) => (
          <li
            key={phase}
            aria-current={i === phaseIndex ? 'step' : undefined}
            className={cn(
              'rounded-md px-3 py-2 text-sm font-semibold',
              i < phaseIndex && 'bg-status-green-bg text-status-green-fg',
              i === phaseIndex && 'bg-brand text-brand-foreground',
              i > phaseIndex && 'bg-muted text-muted-foreground',
            )}
          >
            {PROJECT_PHASE_LABELS[phase]}
            <span className="sr-only">
              {i < phaseIndex ? ' (concluída)' : i === phaseIndex ? ' (fase atual)' : ' (próxima)'}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function ProjectOverview({
  organizationId,
  projectId,
  horizon,
}: {
  organizationId: string;
  projectId: string;
  horizon: [number, number];
}) {
  const { client } = useAuth();
  const can = useCan();
  const ids = { year: useId(), month: useId() };
  // null = o mês padrão da API (o último com dado); o período escolhido (ou do link) fica no endereço (T-144c).
  const [period, setPeriod] = usePeriodParam(horizon);
  const query = useQuery({
    queryKey: ['overview', projectId, period],
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/projects/${projectId}/overview`, {
        query: period ? { year: String(period.year), month: String(period.month) } : {},
        schema: overviewSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });

  if (query.isPending) return <LoadingState rows={6} label="Carregando a visão geral" className="mt-8" />;
  if (query.isError) {
    return (
      <ErrorState
        className="mt-8"
        error={query.error}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  }
  const o = query.data;
  // O período escolhido vale já (os seletores não voltam ao mês antigo enquanto a resposta não chega).
  const shown = period ?? { year: o.year, month: o.month };
  const updating = query.isFetching && query.isPlaceholderData;
  // Drill-down (T-144c, RF-37): cada número leva ao nível de baixo, no mesmo mês.
  const sees = { dashboard: can('dashboard-perspectiva', 'view'), objectives: can('objetivos', 'view') };
  const perspectiveTo = (perspectiveId: string) =>
    perspectiveTarget(
      organizationId,
      { projectId: o.project.id, perspectiveId, year: o.year, month: o.month },
      sees,
    );
  const worst = worstPerspective(o.perspectives);
  const companyTo = worst ? perspectiveTo(worst.id) : null;
  const years = Array.from(
    { length: o.project.horizonEnd - o.project.horizonStart + 1 },
    (_, i) => o.project.horizonStart + i,
  );
  return (
    <div className="mt-6 flex flex-col gap-8" aria-busy={query.isFetching}>
      <MobileHome o={o} organizationId={organizationId} />
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor={ids.month} className="mb-1 block text-xs font-medium text-muted-foreground">
            Mês
          </label>
          <NativeSelect
            id={ids.month}
            value={shown.month}
            onChange={(e) => setPeriod({ year: shown.year, month: Number(e.target.value) })}
          >
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {targetPeriodLabel('MONTH', i + 1)}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div>
          <label htmlFor={ids.year} className="mb-1 block text-xs font-medium text-muted-foreground">
            Ano
          </label>
          <NativeSelect
            id={ids.year}
            value={shown.year}
            onChange={(e) => setPeriod({ year: Number(e.target.value), month: shown.month })}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </NativeSelect>
        </div>
        <p className="pb-2 text-xs text-muted-foreground" aria-live="polite">
          {updating
            ? `Atualizando para ${monthLabel(shown.year, shown.month).toLowerCase()}…`
            : o.computedAt
              ? `Dados de ${monthLabel(o.year, o.month).toLowerCase()}, calculados em ${formatDateTime(o.computedAt)}`
              : `Ainda não há performance calculada em ${monthLabel(o.year, o.month).toLowerCase()}`}
        </p>
      </div>

      <Attention o={o} organizationId={organizationId} />

      <section
        aria-labelledby="performance-estrategica"
        className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]"
      >
        <div className="min-w-0 rounded-lg border border-border bg-surface p-5">
          <h2 id="performance-estrategica" className="text-sm font-semibold text-muted-foreground">
            Performance estratégica
          </h2>
          {o.company && o.company.performance !== null ? (
            <>
              <p className="mt-2 flex items-center gap-3">
                {companyTo && worst ? (
                  <Link
                    to={companyTo}
                    className={cn('text-4xl font-extrabold tabular-nums', DRILL_NUMBER)}
                    aria-label={`Performance estratégica ${pct(o.company.performance)}: aprofundar em ${worst.name}, a perspectiva de menor performance`}
                  >
                    {pct(o.company.performance)}
                  </Link>
                ) : (
                  <span className="text-4xl font-extrabold tabular-nums">{pct(o.company.performance)}</span>
                )}
                <StatusBadge status={o.company.light} />
              </p>
              <Trend value={o.company.trend} />
              {companyTo && worst ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  Clique no número para aprofundar em {worst.name}, a perspectiva de menor performance.
                </p>
              ) : null}
            </>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">
              Sem performance da empresa neste mês: ela aparece quando há metas e resultados lançados.
            </p>
          )}
        </div>
        <div className="min-w-0 rounded-lg border border-border bg-surface p-5">
          <h2 className="text-sm font-semibold text-muted-foreground">Performance por perspectiva</h2>
          {o.perspectives.some((p) => p.performance !== null) ? (
            <ul
              aria-label="Performance por perspectiva"
              className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2"
            >
              {o.perspectives.map((p) => {
                const to = perspectiveTo(p.id);
                const content = (
                  <>
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        className={cn('size-2.5 shrink-0 rounded-full', STATUS_DOT[p.light])}
                        aria-hidden
                      />
                      <span className="truncate font-medium">{p.name}</span>
                    </span>
                    <span className="flex shrink-0 flex-col items-end">
                      <span className="font-bold tabular-nums">
                        {pct(p.performance)}
                        <span className="sr-only"> ({TRAFFIC_LIGHT_LABELS[p.light]})</span>
                      </span>
                      <Trend value={p.trend} compact />
                    </span>
                  </>
                );
                const box = 'flex items-center justify-between gap-3 rounded-md bg-muted/50 px-3 py-2';
                return (
                  <li key={p.id}>
                    {to ? (
                      <Link to={to} className={cn(box, 'hover:bg-muted')}>
                        {content}
                        <ChevronRight aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                      </Link>
                    ) : (
                      <div className={box}>{content}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">Sem performance das perspectivas neste mês.</p>
          )}
        </div>
      </section>

      <Cards o={o} organizationId={organizationId} />
    </div>
  );
}

function Trend({ value, compact = false }: { value: number | null; compact?: boolean }) {
  if (value === null) {
    return compact ? null : (
      <p className="mt-1 text-sm text-muted-foreground">Sem o mês anterior para comparar.</p>
    );
  }
  const Icon = value > 0 ? ArrowUpRight : value < 0 ? ArrowDownRight : Minus;
  const text = `${value > 0 ? '+' : ''}${formatDecimal(value, 1)} p.p. vs mês anterior`;
  return (
    <p
      className={cn(
        'flex items-center gap-1 tabular-nums',
        compact ? 'text-xs' : 'mt-1 text-sm',
        value > 0 ? 'text-status-green-fg' : value < 0 ? 'text-status-red-fg' : 'text-muted-foreground',
      )}
    >
      <Icon className="size-4" aria-hidden />
      {text}
    </p>
  );
}

/**
 * Home mobile do executivo (PRD §11.30; T-146a, DEC-060): no celular, o resumo no topo da Visão Geral — saudação,
 * performance estratégica, objetivos críticos, KPIs em atenção, ações atrasadas e "Ver Dashboard". Os números são os
 * da Visão Geral (mesmo mês), e cada um leva à sua lista. No desktop não aparece.
 */
function MobileHome({ o, organizationId }: { o: Overview; organizationId: string }) {
  const can = useCan();
  const user = useCurrentUser();
  const path = { projectId: o.project.id, year: o.year, month: o.month };
  const period = `projeto=${o.project.id}&ano=${o.year}&mes=${o.month}`;
  const dashboard = can('dashboard-executivo', 'view')
    ? `/empresas/${organizationId}/dashboards/executivo?${period}`
    : can('dashboard-perspectiva', 'view')
      ? `/empresas/${organizationId}/dashboards/perspectiva?${period}`
      : can('dashboard-area', 'view')
        ? `/empresas/${organizationId}/dashboards/area?${period}`
        : null;
  const items: { key: string; value: number; label: string; to: string | null; alert: boolean }[] = [];
  if (o.cards.objectives) {
    const n = o.cards.objectives.critical;
    items.push({
      key: 'objetivos',
      value: n,
      label: n === 1 ? 'objetivo crítico' : 'objetivos críticos',
      to: can('mapa-estrategico', 'view') ? drillHref.map(organizationId, path) : null,
      alert: n > 0,
    });
  }
  if (o.cards.kpis) {
    const n = o.cards.kpis.offTarget;
    items.push({
      key: 'kpis',
      value: n,
      label: n === 1 ? 'KPI em atenção' : 'KPIs em atenção',
      to: can('metas-resultados', 'view') ? resultsLink(organizationId, o, 'fora-da-meta') : null,
      alert: n > 0,
    });
  }
  if (o.cards.actions) {
    const n = o.cards.actions.late;
    items.push({
      key: 'acoes',
      value: n,
      label: n === 1 ? 'ação atrasada' : 'ações atrasadas',
      to: `/empresas/${organizationId}/execucao/planos-acao?projeto=${o.project.id}&atrasadas=1`,
      alert: n > 0,
    });
  }
  return (
    <section
      aria-label="Resumo do dia"
      className="flex flex-col gap-4 rounded-lg border border-border bg-surface p-5 md:hidden"
    >
      <p className="text-xl font-bold">
        {greetingFor(new Date())}, {firstName(user.name)}.
      </p>
      <div>
        <p className="text-sm font-semibold text-muted-foreground">Performance estratégica</p>
        {o.company && o.company.performance !== null ? (
          <p className="mt-1 flex items-center gap-3">
            <span className="text-4xl font-extrabold tabular-nums">{pct(o.company.performance)}</span>
            <StatusBadge status={o.company.light} />
          </p>
        ) : (
          <p className="mt-1 text-sm text-muted-foreground">Ainda sem performance calculada.</p>
        )}
        <p className="mt-1 text-xs text-muted-foreground">{monthLabel(o.year, o.month)}</p>
      </div>
      {items.length ? (
        <ul className="flex flex-col divide-y divide-border">
          {items.map((i) => {
            const body = (
              <>
                <span className={cn('text-2xl font-extrabold tabular-nums', i.alert && 'text-status-red-fg')}>
                  {i.value}
                </span>
                {/* Espaço real entre o número e o rótulo: o texto (e o nome lido) fica "7 KPIs em atenção". */}{' '}
                <span className="flex-1">{i.label}</span>
                {i.to ? <ChevronRight aria-hidden className="size-5 text-muted-foreground" /> : null}
              </>
            );
            return (
              <li key={i.key}>
                {i.to ? (
                  <Link
                    to={i.to}
                    aria-label={`${i.value} ${i.label}`}
                    className="flex min-h-11 items-center gap-3 py-1"
                  >
                    {body}
                  </Link>
                ) : (
                  <div className="flex min-h-11 items-center gap-3 py-1">{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
      {dashboard ? (
        <Button asChild className="h-11 w-full">
          <Link to={dashboard}>Ver Dashboard</Link>
        </Button>
      ) : null}
    </section>
  );
}

/** Resultados do mês filtrados pelo semáforo (destino dos KPIs da Visão Geral). */
const resultsLink = (organizationId: string, o: Overview, light: 'fora-da-meta' | 'vermelho' | 'amarelo') =>
  `/empresas/${organizationId}/indicadores/resultados?projeto=${o.project.id}&ano=${o.year}&mes=${o.month}&semaforo=${light}`;

function Cards({ o, organizationId }: { o: Overview; organizationId: string }) {
  const can = useCan();
  const ws = (path: string) => `/empresas/${organizationId}/${path}`;
  const cards: { label: string; value: number; icon: LucideIcon; to: string | null; tone?: TrafficLight }[] =
    [];
  if (o.cards.objectives) {
    cards.push(
      {
        label: 'Objetivos estratégicos',
        value: o.cards.objectives.total,
        icon: Target,
        to: ws('estrategia/objetivos'),
      },
      {
        label: 'Objetivos críticos',
        value: o.cards.objectives.critical,
        icon: AlertTriangle,
        to: can('mapa-estrategico', 'view')
          ? drillHref.map(organizationId, { projectId: o.project.id, year: o.year, month: o.month })
          : null,
        tone: o.cards.objectives.critical ? 'red' : 'green',
      },
    );
  }
  if (o.cards.kpis) {
    cards.push(
      { label: 'KPIs ativos', value: o.cards.kpis.active, icon: Gauge, to: ws('indicadores/kpis') },
      {
        label: 'KPIs fora da meta',
        value: o.cards.kpis.offTarget,
        icon: TrendingDown,
        to: can('metas-resultados', 'view') ? resultsLink(organizationId, o, 'fora-da-meta') : null,
        tone: o.cards.kpis.offTarget ? 'yellow' : 'green',
      },
    );
  }
  if (o.cards.initiatives) {
    cards.push({
      label: 'Iniciativas abertas',
      value: o.cards.initiatives.open,
      icon: Rocket,
      to: ws('execucao/iniciativas'),
    });
  }
  if (o.cards.actions) {
    cards.push({
      label: 'Ações atrasadas',
      value: o.cards.actions.late,
      icon: ListChecks,
      to: ws(`execucao/planos-acao?projeto=${o.project.id}&atrasadas=1`),
      tone: o.cards.actions.late ? 'red' : 'green',
    });
  }
  if (!cards.length) return null;
  return (
    <section aria-labelledby="numeros-estrategia">
      <h2 id="numeros-estrategia" className="text-base font-bold">
        Números da estratégia
      </h2>
      <ul className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {cards.map((c) => {
          const body = (
            <>
              <c.icon className="size-5 text-muted-foreground" aria-hidden />
              <span className="text-2xl font-extrabold tabular-nums">{c.value}</span>
              {/* O nome do cartão é o próprio texto (número e rótulo, como na tela — WCAG 2.5.3): "12 Objetivos
                  estratégicos". */}{' '}
              <span className="text-sm text-muted-foreground">{c.label}</span>
            </>
          );
          const cls = cn(
            'flex h-full flex-col gap-1 rounded-lg border bg-surface p-4',
            c.tone === 'red' && c.value ? 'border-status-red-solid/50' : 'border-border',
          );
          return (
            <li key={c.label}>
              {c.to ? (
                <Link to={c.to} className={cn(cls, 'hover:bg-muted/50')}>
                  {body}
                </Link>
              ) : (
                <div className={cls}>{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

type AttentionItem = {
  key: string;
  title: string;
  detail: string;
  to: string | null;
  tone: TrafficLight;
  /** O número do item com destino próprio (o nível de baixo — drill-down, T-144c). */
  value?: { text: string; to: string; label: string } | null;
};
type AttentionGroup = {
  key: string;
  label: string;
  icon: LucideIcon;
  total: number;
  items: AttentionItem[];
  all: { label: string; to: string } | null;
};

/** "Requer atenção" (PRD §5.4, §11.3, §22): grupos com o total e os mais graves; cada item leva ao detalhe. */
function Attention({ o, organizationId }: { o: Overview; organizationId: string }) {
  const can = useCan();
  const p = o.project.id;
  const ws = (path: string) => `/empresas/${organizationId}/${path}`;
  const seesResults = can('metas-resultados', 'view');
  const unitsValue = (kpiId: string, code: string, performance: number | null) =>
    seesResults
      ? {
          text: pct(performance),
          to: drillHref.unit(organizationId, { projectId: p, kpiId, year: o.year, month: o.month }),
          label: `${code} ${pct(performance)}: ver por unidade`,
        }
      : null;
  const kpiLink = (kpiId: string) =>
    // Com o mês da visão (drill-down — T-144c).
    seesResults ? drillHref.kpi(organizationId, { projectId: p, kpiId, year: o.year, month: o.month }) : null;
  const a = o.attention;
  const groups: AttentionGroup[] = [];
  if (a.criticalObjectives) {
    groups.push({
      key: 'objetivos',
      label: 'Objetivos críticos',
      icon: Target,
      total: a.criticalObjectives.total,
      items: a.criticalObjectives.items.map((x) => ({
        key: x.id,
        title: x.name,
        detail: `${x.perspective} · ${pct(x.performance)}`,
        to: drillHref.objective(organizationId, {
          projectId: p,
          objectiveId: x.id,
          year: o.year,
          month: o.month,
        }),
        tone: 'red',
      })),
      all: can('mapa-estrategico', 'view')
        ? {
            label: 'Ver no mapa estratégico',
            to: drillHref.map(organizationId, { projectId: p, year: o.year, month: o.month }),
          }
        : null,
    });
  }
  if (a.criticalKpis) {
    groups.push({
      key: 'kpis-criticos',
      label: 'Indicadores críticos',
      icon: Gauge,
      total: a.criticalKpis.total,
      items: a.criticalKpis.items.map((x) => ({
        key: x.kpiId,
        title: `${x.code} · ${x.name}`,
        // O % fica só no número ao lado (que leva às unidades).
        detail: 'Abaixo da meta no mês',
        to: kpiLink(x.kpiId),
        tone: 'red',
        value: unitsValue(x.kpiId, x.code, x.performance),
      })),
      all: seesResults
        ? { label: 'Ver os KPIs em vermelho', to: resultsLink(organizationId, o, 'vermelho') }
        : null,
    });
  }
  if (a.fallingKpis) {
    const seesAlerts = can('alertas', 'view');
    groups.push({
      key: 'kpis-queda',
      label: 'KPIs em queda',
      icon: TrendingDown,
      total: a.fallingKpis.total,
      items: a.fallingKpis.items.map((x) => ({
        key: x.alertId,
        title: `${x.code} · ${x.name}`,
        detail: `${ALERT_CRITICALITY_LABELS[x.criticality]} · ${x.message}`,
        to: seesAlerts ? ws(`gestao/alertas?projeto=${p}&alerta=${x.alertId}`) : kpiLink(x.kpiId),
        tone: x.criticality === 'CRITICAL' || x.criticality === 'HIGH' ? 'red' : 'yellow',
      })),
      all: seesAlerts
        ? { label: 'Ver na central de alertas', to: ws(`gestao/alertas?projeto=${p}&tipo=CONSECUTIVE_DROP`) }
        : null,
    });
  }
  if (a.lateActions) {
    groups.push({
      key: 'acoes',
      label: 'Ações atrasadas',
      icon: ListChecks,
      total: a.lateActions.total,
      items: a.lateActions.items.map((x) => ({
        key: x.id,
        title: x.title,
        detail: `${x.planName} · prazo ${formatDate(x.dueDate)}${x.responsibleName ? ` · ${x.responsibleName}` : ''}`,
        to: actionLink(organizationId, p, x.planId, x.id),
        tone: 'red',
      })),
      all: { label: 'Ver as ações atrasadas', to: ws(`execucao/planos-acao?projeto=${p}&atrasadas=1`) },
    });
  }
  if (a.nearLimitTargets) {
    groups.push({
      key: 'metas',
      label: 'Metas próximas do limite',
      icon: AlertTriangle,
      total: a.nearLimitTargets.total,
      items: a.nearLimitTargets.items.map((x) => ({
        key: x.kpiId,
        title: `${x.code} · ${x.name}`,
        detail: `Vermelho abaixo de ${pct(x.redBelow)}`,
        to: kpiLink(x.kpiId),
        tone: 'yellow',
        value: unitsValue(x.kpiId, x.code, x.performance),
      })),
      all: seesResults
        ? { label: 'Ver os KPIs em amarelo', to: resultsLink(organizationId, o, 'amarelo') }
        : null,
    });
  }
  if (a.pendingMeetings) {
    groups.push({
      key: 'reunioes',
      label: 'Reuniões pendentes',
      icon: CalendarClock,
      total: a.pendingMeetings.total,
      items: a.pendingMeetings.items.map((x) => ({
        key: x.id,
        title: x.title,
        detail: `Era em ${formatDateTime(x.startsAt)} e não foi iniciada`,
        to: meetingLink(organizationId, p, x.id),
        tone: 'yellow',
      })),
      all: { label: 'Ver as reuniões pendentes', to: ws(`gestao/reunioes?projeto=${p}&lista=pendentes`) },
    });
  }
  if (a.untreatedRisks) {
    groups.push({
      key: 'riscos',
      label: 'Riscos sem tratamento',
      icon: ShieldAlert,
      total: a.untreatedRisks.total,
      items: a.untreatedRisks.items.map((x) => ({
        key: x.id,
        title: x.description,
        detail: `${x.initiative.name} · impacto ${RISK_IMPACT_LABELS[x.impact].toLowerCase()}, probabilidade ${RISK_PROBABILITY_LABELS[x.probability].toLowerCase()}`,
        to: ws(`execucao/iniciativas?projeto=${p}&iniciativa=${x.initiative.id}`),
        tone: x.impact === 'HIGH' ? 'red' : 'yellow',
      })),
      all: {
        label: 'Ver as iniciativas com risco sem tratamento',
        to: ws(`execucao/iniciativas?projeto=${p}&riscos=sem-tratamento`),
      },
    });
  }
  if (a.pendingDecisions) {
    groups.push({
      key: 'decisoes',
      label: 'Decisões pendentes',
      icon: CheckCircle2,
      total: a.pendingDecisions.total,
      items: a.pendingDecisions.items.map((x) => ({
        key: x.id,
        title: x.text,
        detail: `${x.meeting.title}${x.dueDate ? ` · prazo ${formatDate(x.dueDate)}` : ''}${x.late ? ' (vencido)' : ''}${x.responsibleName ? ` · ${x.responsibleName}` : ''}`,
        to: meetingLink(organizationId, p, x.meeting.id),
        tone: x.late ? 'red' : 'yellow',
      })),
      all: null,
    });
  }
  const open = groups.filter((g) => g.total > 0);
  return (
    <section aria-labelledby="requer-atencao" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="requer-atencao" className="text-lg font-bold">
          Requer atenção
        </h2>
        {groups.length ? (
          <p className="text-xs text-muted-foreground">
            {groups.map((g) => `${g.label}: ${g.total}`).join(' · ')}
          </p>
        ) : null}
      </div>
      {!groups.length ? (
        <p className="rounded-lg border border-dashed border-border px-4 py-6 text-sm text-muted-foreground">
          Seu perfil não acompanha objetivos, indicadores, ações, reuniões nem iniciativas.
        </p>
      ) : !open.length ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-8"
          icon={CheckCircle2}
          title="Nada precisa de atenção agora"
          description="Sem objetivos ou indicadores críticos, ações atrasadas, metas perto do vermelho, reuniões pendentes, riscos sem tratamento ou decisões pendentes no seu escopo."
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {open.map((g) => (
            <AttentionCard key={g.key} group={g} />
          ))}
        </div>
      )}
    </section>
  );
}

function AttentionCard({ group }: { group: AttentionGroup }) {
  const titleId = useId();
  const more = group.total - group.items.length;
  return (
    <article
      aria-labelledby={titleId}
      className="flex min-w-0 flex-col gap-2 rounded-lg border border-border bg-surface p-4"
    >
      <h3 id={titleId} className="flex items-center gap-2 text-sm font-bold">
        <group.icon className="size-4 text-muted-foreground" aria-hidden />
        {group.label}
        <span className="ml-auto rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums">{group.total}</span>
      </h3>
      <ul aria-label={group.label} className="flex flex-col divide-y divide-border">
        {group.items.map((item) => {
          const content: ReactNode = (
            <>
              <span
                className={cn('mt-1.5 size-2 shrink-0 rounded-full', STATUS_DOT[item.tone])}
                aria-hidden
              />
              <span className="min-w-0 flex-1">
                <span className="block break-words font-medium">{item.title}</span>
                <span className="block break-words text-xs text-muted-foreground">{item.detail}</span>
              </span>
            </>
          );
          return (
            <li key={item.key} className="flex items-start gap-2">
              {item.to ? (
                <Link
                  to={item.to}
                  className="flex min-w-0 flex-1 gap-2 rounded-md px-1 py-2 hover:bg-muted/50"
                >
                  {content}
                  {item.value ? null : (
                    <ArrowRight className="mt-1 size-4 shrink-0 text-muted-foreground" aria-hidden />
                  )}
                </Link>
              ) : (
                <div className="flex min-w-0 flex-1 gap-2 px-1 py-2">{content}</div>
              )}
              {item.value ? (
                <span className="py-2">
                  <DrillValueLink to={item.value.to} label={item.value.label}>
                    {item.value.text}
                  </DrillValueLink>
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
      {more > 0 || group.all ? (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
          {more > 0 ? <span className="text-muted-foreground">e mais {more}</span> : <span />}
          {group.all ? (
            <Link
              to={group.all.to}
              className="inline-flex items-center font-medium text-brand-strong underline underline-offset-2 dark:text-brand"
            >
              {group.all.label}
            </Link>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
